import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';

import type UiInputGroupLabelComponent from './input-group/label';
import type UiInputGroupHintComponent from './input-group/hint';
import type UiInputGroupInputComponent from './input-group/input';
import type UiInputGroupNumberInputComponent from './input-group/number-input';
import type UiInputGroupTextFieldComponent from './input-group/text-field';
import type UiInputGroupNativeSelectComponent from './input-group/native-select';
import type UiInputGroupCodeEditorComponent from './input-group/code-editor';
import type UiInputGroupDescriptionComponent from './input-group/description';
import type UiInputGroupStringListComponent from './input-group/string-list';
import type UiInputGroupTextareaComponent from './input-group/textarea';
import type UiInputGroupRichTextEditorComponent from './input-group/rich-text-editor';
import type UiInputGroupWysiwygEditorComponent from './input-group/wysiwyg-editor';
import { type WithBoundArgs } from '@glint/template';

interface UiInputGroupSignature {
  Element: HTMLDivElement;
  Args: {
    Named: {
      id?: string;
      errors?: string[];
      touched?: boolean;
    };
  };
  Blocks: {
    default: [
      Partial<{
        Label: WithBoundArgs<typeof UiInputGroupLabelComponent, 'for'>;
        Hint: WithBoundArgs<typeof UiInputGroupHintComponent, 'id'>;
        Input: WithBoundArgs<
          typeof UiInputGroupInputComponent,
          'id' | 'ariaDescribedby' | 'hasError' | 'iconRight'
        >;
        NumberInput: WithBoundArgs<
          typeof UiInputGroupNumberInputComponent,
          'id' | 'ariaDescribedby' | 'hasError' | 'iconRight'
        >;
        TextField: WithBoundArgs<
          typeof UiInputGroupTextFieldComponent,
          'id' | 'ariaDescribedby'
        >;
        NativeSelect: WithBoundArgs<
          typeof UiInputGroupNativeSelectComponent,
          'id' | 'ariaDescribedby'
        >;
        CodeEditor: WithBoundArgs<
          typeof UiInputGroupCodeEditorComponent,
          'id' | 'ariaDescribedby' | 'ariaLabelledby' | 'hasError' | 'iconRight'
        >;
        Description: WithBoundArgs<
          typeof UiInputGroupDescriptionComponent,
          'id' | 'errorMessage' | 'showError'
        >;
        StringList: WithBoundArgs<
          typeof UiInputGroupStringListComponent,
          'id' | 'ariaDescribedby' | 'hasError'
        >;
        Textarea: WithBoundArgs<
          typeof UiInputGroupTextareaComponent,
          'id' | 'ariaDescribedby' | 'hasError' | 'iconRight'
        >;
        RichTextEditor: WithBoundArgs<
          typeof UiInputGroupRichTextEditorComponent,
          'id' | 'ariaDescribedby' | 'hasError'
        >;
        WysiwygEditor: WithBoundArgs<
          typeof UiInputGroupWysiwygEditorComponent,
          'id' | 'ariaDescribedby' | 'ariaLabelledby' | 'hasError'
        >;
      }>
    ];
  };
}

export default class UiInputGroup extends Component<UiInputGroupSignature> {
  get touched() {
    return this.args.touched ?? false;
  }

  get id() {
    return this.args.id || `${guidFor(this)}-input-group`;
  }

  get showError() {
    return this.touched && this.hasError;
  }

  get hasError() {
    return !!this.errorMessage;
  }

  get errorMessage() {
    return this.args.errors?.[0] || '';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::InputGroup': typeof UiInputGroup;
    'ui/input-group': typeof UiInputGroup;
  }
}
